import { z } from "zod";

export const EVENTS = {
  auth_signup_view: "Sign-up form rendered",
  auth_signup_submit: "Sign-up form submitted",
  auth_signup_succeeded: "Sign-up completed",
  auth_signup_failed: "Sign-up failed",
  auth_email_verify_requested: "Email verification code requested",
  auth_email_verified: "Email verification succeeded",
  auth_email_verify_failed: "Email verification failed",
  auth_resend_verification_succeeded: "Verification code resent",
  auth_resend_verification_failed: "Verification code resend failed",
  auth_login_view: "Sign-in form rendered",
  auth_login_submit: "Sign-in form submitted",
  auth_login_succeeded: "Sign-in succeeded",
  auth_login_failed: "Sign-in failed",
  auth_google_oauth_started: "Google OAuth started",
  auth_logout: "User logged out",
  auth_forgot_password_view: "Forgot password form rendered",
  auth_forgot_password_submit: "Forgot password submitted",
  auth_forgot_password_succeeded: "Forgot password succeeded",
  auth_reset_password_view: "Reset password form rendered",
  auth_reset_password_submit: "Reset password submitted",
  auth_reset_password_succeeded: "Reset password succeeded",
  billing_view: "Billing page viewed",
  pricing_view: "Pricing page viewed",
  pricing_plan_card_clicked: "Pricing plan card clicked",
  pricing_cta_clicked: "Pricing CTA clicked",
  checkout_started: "Checkout flow started",
  checkout_payment_initiated: "Payment initiated",
  checkout_payment_succeeded: "Payment succeeded",
  checkout_payment_failed: "Payment failed",
  renewal_authorization_started: "Renewal authorization started",
  downgrade_scheduled: "Downgrade scheduled",
  downgrade_cancelled: "Downgrade cancelled",
  api_keys_view: "API keys page viewed",
  api_key_create_modal_opened: "Create API key modal opened",
  api_key_create_submitted: "Create API key submitted",
  api_key_created: "API key created",
  api_key_create_failed: "API key creation failed",
  api_key_copied: "API key copied",
  api_key_revoke_modal_opened: "Revoke API key modal opened",
  api_key_revoked: "API key revoked",
  api_key_revoke_failed: "API key revoke failed",
  proxies_view: "Proxies page viewed",
  proxy_rotate_clicked: "Proxy rotate clicked",
  proxy_rotated: "Proxy rotated",
  proxy_rotated_failed: "Proxy rotation failed",
  proxy_copy_endpoint: "Proxy endpoint copied",
  proxy_test_connection: "Proxy connection tested",
  proxy_usage_chart_viewed: "Proxy usage chart viewed",
  dashboard_view: "Dashboard viewed",
  nav_link_clicked: "Navigation link clicked",
  cta_clicked: "Call-to-action clicked",
  upgrade_prompt_shown: "Upgrade prompt shown",
  upgrade_prompt_clicked: "Upgrade prompt clicked",
  settings_view: "Settings page viewed",
  settings_saved: "Settings saved",
  support_link_clicked: "Support link clicked",
  api_error: "API error",
  react_error_boundary_caught: "React error boundary caught",
  page_view: "Page view",
  onboarding_view: "Onboarding questionnaire rendered",
  onboarding_step_completed: "Onboarding questionnaire step completed",
  onboarding_submitted: "Onboarding questionnaire submitted",
  onboarding_skipped: "Onboarding questionnaire skipped",
} as const;

export const SOURCES = {
  autoPageView: "auto:page_view",
  signInForm: "sign-in-form",
  signUpForm: "sign-up-form",
  verifyForm: "verify-form",
  forgotPasswordForm: "forgot-password-form",
  resetPasswordForm: "reset-password-form",
  billingClient: "billing-client",
  pricingPage: "pricing-page",
  proxiesPage: "proxies-page",
  apiKeyList: "api-key-list",
  dashboardPage: "dashboard-page",
  dashboardLayout: "dashboard-layout",
  errorBoundary: "error-boundary",
  apiClient: "api-client",
  headerNav: "header-nav",
  pricingCta: "pricing-cta",
  upgradePrompt: "upgrade-prompt",
  settingsPage: "settings-page",
  fromRedirect: "from-redirect",
  onboardingSheet: "onboarding-sheet",
} as const;

export type SnowpadEvent = keyof typeof EVENTS;
export type EventSource = (typeof SOURCES)[keyof typeof SOURCES];

export const KNOWN_EVENTS = Object.keys(EVENTS) as [string, ...string[]];
export const KNOWN_SOURCES = Object.values(SOURCES) as [string, ...string[]];

/** Strict payload schema — rejects unknown events or sources immediately */
export const eventPayloadSchema = z.object({
  event: z.enum(KNOWN_EVENTS),
  source: z.enum(KNOWN_SOURCES),
  params: z
    .record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
    .refine((val) => Object.keys(val).length <= 20, { message: "Too many params (max 20)" })
    .optional(),
  route: z.string().max(500).optional(),
  ga_client_id: z.string().optional(),
});

export type EventPayload = z.infer<typeof eventPayloadSchema>;
